import { PeriodType } from '../widget/date-range.widget.model';

import {
    Category,
    Config,
    ConfigPath,
    ConfigView,
    ConfigViewMetric,
    ConfigViewPillar,
    DataKey,
    DefinitionSummary,
    HierarchyUserInfoItem,
    Metric,
    Override,
    RoleHierarchyItem,
    TrendPeriodStart,
} from './config.insight.model';
import { ComponentType } from './enums/component-type.enum';
import { Sort } from './experimental.grid';
import { MetricThreshold, PerformanceTrend } from './legend.insight.model';
import { OptionalColumn } from './optional-column.insight.model';
import { CacheDateItem } from './performance-data/cache-date.pd.model';
import {
    DataFilterOptions,
    FilterContext,
} from './performance-data/data-filter-options.pd.model';
import { MetricData, MetricDataItem } from './performance-data/metric.pd.model';
import { PresentationView } from './presentation.insight.model';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface PersistState {}

export interface PeriodState {
    pending: {
        periodType?: boolean;
    };
    periodType?: PeriodType;
    cacheDates?: CacheDateItem[];
    eventDate?: Date;
}

export interface VisualState {
    pending: {
        data?: boolean;
    };
    data?: MetricData;
    peerGroupData?: MetricData;
}

export interface GridState {
    pending: {
        data: { [rowId: string]: boolean };
    };
    supportedColumns?: OptionalColumn[];
    activeColumns?: OptionalColumn[];
    data?: DataKey[];
    peerGroupData?: DataKey[];
    hierarchyUserInfo?: HierarchyUserInfoItem[];
    hierarchyUserInfoCurrent?: HierarchyUserInfoItem[];
    lastToggleColumnState?: {
        columnId: string;
        pillarExpanded: boolean;
        metricExpanded: boolean;
    };
    sort?: {
        rowIds: string[];
        def: Sort;
    };
    export?: Date;
}

export interface TrendState {
    pending: {
        data: { [rowId: string]: boolean };
    };
    supportedColumns?: OptionalColumn[];
    activeColumns?: OptionalColumn[];
    periods?: PeriodType[];
    periodType?: PeriodType;
    startDates?: TrendPeriodStart[];
    data?: DataKey[];
    peerGroupData?: DataKey[];
}

export interface LegendState {
    metricThresholds?: MetricThreshold[];
    trendsArrows?: PerformanceTrend[];
}

export type MetricPath = [ConfigViewPillar, Metric, ConfigViewMetric[]];

export interface BaseState {
    pending: {
        summary?: boolean;
        config?: boolean;
        configReload?: boolean;
        supportedColumns?: boolean;
    };
    summary?: Category[];
    activeCategory?: Category;
    activeSubCategory?: Category;
    activeDefinition?: DefinitionSummary;
    override?: Override;
    activeConfig?: string;
    configs: {
        [key: string]: Config;
    };
    component?: ComponentType;
    configUserInfo?: HierarchyUserInfoItem;
    activeView?: ConfigView;
    viewContext?: FilterContext;
    defaultPath?: string;
    activePath?: ConfigPath;
    initialDataFilterOpts?: DataFilterOptions;
    roleHierarchy?: RoleHierarchyItem[];
    cacheLangId?: number; // TODO revise? for when lang has changed, whether or not to reload optional columns
    optionalColumns?: OptionalColumn[];
    activeViewPillar?: ConfigViewPillar;
    activeViewMetric?: ConfigViewMetric;
    activeMetric?: Metric;
    activeDataMetric?: MetricDataItem;
    activePillars?: ConfigViewPillar[];
    activeMetrics?: MetricPath[];
    presentAs: PresentationView;
    trendHideFirstGridColumn?: boolean;

    persist: PersistState;
    period: PeriodState;
    visual: VisualState;
    grid: GridState;
    trend: TrendState;
    legend: LegendState;
}

export const defaultPresentationView = PresentationView.GRID;

export const defaultBaseState = (): BaseState => ({
    pending: {},
    configs: {},
    presentAs: defaultPresentationView,

    persist: {},
    period: {
        pending: {},
        periodType: PeriodType.MONTH,
    },
    visual: {
        pending: {},
    },
    grid: {
        pending: {
            data: {},
        },
    },
    trend: {
        pending: {
            data: {},
        },
    },
    legend: {
        metricThresholds: [],
        trendsArrows: [],
    },
});
